import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'react-feather';
import { twMerge } from 'tailwind-merge';
import { COLORS } from '../../../../utils/colors';
import { formatMoney } from '@dill/dill-shared/dist/src';

type PlaidSummaryType = {
  date: string;
  amount: number;
  numberOfTransactions: number;
  transactions: {
    name?: string | null;
    description?: string | null;
    date: string;
    amount: number;
    detailedCategory?: string | null;
  }[];
};

const CONTENT_WIDTH = 460;

const columns = [
  { id: 'month', name: 'Month', width: 140 },
  { id: 'amount', name: 'Cumulative Amount', width: 180 },
  { id: 'transactions', name: 'Transactions', width: 100 },
];

const totalWidth = columns.reduce((sum, col) => sum + col.width, 0);

const PlaidSummary = ({
  summary,
  label,
  onSelectSummary,
}: {
  summary: PlaidSummaryType[];
  label: string;
  onSelectSummary: (summary: PlaidSummaryType) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    let animationFrameId: number;

    const updatePosition = () => {
      if (!isOpen || !buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();
      const isInViewport =
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;

      if (isInViewport) {
        const dropdownHeight = dropdownRef.current?.offsetHeight || 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const shouldOpenUpward = spaceBelow < dropdownHeight + 20 && spaceAbove > spaceBelow;

        setDropdownPosition({
          top: shouldOpenUpward ? rect.top - dropdownHeight - 5 : rect.bottom + 6,
          left: rect.left,
        });

        animationFrameId = requestAnimationFrame(updatePosition);
      } else {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      animationFrameId = requestAnimationFrame(updatePosition);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isOpen]);

  return (
    <>
      <div className="relative w-full" ref={buttonRef}>
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-150 text-left">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-800">{label}</span>
            {summary.length > 0 && (
              <span className="text-xs font-medium text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded-full">
                {summary.length}
              </span>
            )}
          </div>
          <div
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
            <ChevronDown size={16} color={COLORS.GREY_500} />
          </div>
        </button>
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: CONTENT_WIDTH,
              zIndex: 9999,
            }}
            className="border PRIMARY_50-BG py-2 rounded shadow-xl">
            {/* Header */}
            <div className="w-full flex border-b justify-between px-4 sticky top-0 PRIMARY_50-BG items-center z-10">
              {columns.map((col, i) => (
                <div
                  key={col.id + i}
                  style={{ flex: col.width / totalWidth }}
                  className={twMerge('flex py-2 mx-0.5 items-center')}>
                  <p className="text-xs TEXT_SECONDARY-CLR">{col.name}</p>
                </div>
              ))}
            </div>

            {/* Body */}
            <div className="w-full max-h-[400px] WHITE-BG overflow-y-scroll">
              {summary.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No data available</p>
              ) : (
                summary.map((row, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      onSelectSummary(row);
                      setIsOpen(false);
                    }}
                    className="w-full flex border-b border-b-gray-200 px-4 hover:bg-blue-50 cursor-pointer transition-colors duration-100">
                    {columns.map((col, i) => (
                      <div
                        key={col.id + i}
                        style={{ flex: col.width / totalWidth }}
                        className="flex py-3 mx-0.5 items-center">
                        {col.id === 'month' && (
                          <p className="text-xs text-gray-800 capitalize">{row.date}</p>
                        )}
                        {col.id === 'amount' && (
                          <p className="text-xs text-gray-800 tabular-nums">
                            {formatMoney(row.amount ?? 0, 2)}
                          </p>
                        )}
                        {col.id === 'transactions' && (
                          <p className="text-xs text-gray-500 tabular-nums">
                            {row.numberOfTransactions}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default PlaidSummary;
